'use client';
import { ChakraProvider } from '@chakra-ui/react';
import { datadogRum } from '@datadog/browser-rum';
import { SSOProvider, useSSO } from '@orca/contexts-sso';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppProps } from 'next/app';
import { ReCaptchaProvider } from 'next-recaptcha-v3';
import { useMemo } from 'react';

import { GlobalErrorBoundary } from '~/components/GlobalErrorBoundary';
import { FEATURE_FLAGS } from '~/constants/featureFlags';
import { routes } from '~/constants/routes';
import { AlertProvider } from '~/contexts/alert';
import { AuthProvider } from '~/contexts/auth';
import { LegacyProvider } from '~/contexts/legacy';
import { ModalProvider } from '~/contexts/modal';
import { useFeature } from '~/hooks/useFeature';
import customTheme from '~/styles/theme';
import Fonts from '~/styles/theme.fonts';

function UserTrackingManager() {
  const { user } = useSSO();
  if (user) {
    datadogRum.setUser({
      id: user.id,
      name: user.username,
      email: user.email,
    });
  }
  return null;
}

function AppWithProviders({ Component, pageProps, router }: AppProps) {
  const { isEnabled: isSsoRevampEnabled, status: isSsoRevampStatus } =
    useFeature(FEATURE_FLAGS.ssoRevamp);

  // TODO: once sso_revamp feature flag is enabled, remove the old login and logout urls
  const redirectToLogin = useMemo(() => {
    return (redirectUrl?: string) => {
      if (isSsoRevampEnabled) {
        // Same domain - use Next.js router with just /login
        router.push({
          pathname: routes.login,
          query: redirectUrl ? { redirectUrl } : {},
        });
      } else {
        // Cross-domain - use window.location
        const loginUrl = new URL(routes.ssoLogin);
        if (redirectUrl) {
          loginUrl.searchParams.append('redirectUrl', redirectUrl);
        }
        window.location.assign(loginUrl);
      }
    };
  }, [isSsoRevampEnabled, router]);

  const onLogout = useMemo(() => {
    return () => {
      if (isSsoRevampEnabled) {
        // Same domain - redirect to login using window.location for immediate redirect
        window.location.assign(routes.login);
      } else {
        // Cross-domain - redirect to SSO logout
        window.location.assign(routes.ssoLogout);
      }
    };
  }, [isSsoRevampEnabled]);

  // Don't render until feature flag is loaded
  if (isSsoRevampStatus === 'loading') {
    return null;
  }

  return (
    <SSOProvider redirectToLogin={redirectToLogin} onLogout={onLogout}>
      <ReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}>
        <AuthProvider>
          <UserTrackingManager />
          <Fonts />
          <LegacyProvider>
            <ModalProvider>
              <AlertProvider>
                <Component {...pageProps} router={router} />
              </AlertProvider>
            </ModalProvider>
          </LegacyProvider>
        </AuthProvider>
      </ReCaptchaProvider>
    </SSOProvider>
  );
}

function MyApp({ Component, pageProps, router }: AppProps) {
  const queryClient = useMemo(() => new QueryClient(), []);

  return (
    <ChakraProvider theme={customTheme} resetCSS>
      <GlobalErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {process.env.NODE_ENV !== 'production' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}

          <AppWithProviders
            Component={Component}
            pageProps={pageProps}
            router={router}
          />
        </QueryClientProvider>
      </GlobalErrorBoundary>
    </ChakraProvider>
  );
}

export default MyApp;
